import { AuthenticationModalV2Props } from '@components/atoms/authentication/authentication-modal/authentication-modal-v2';
import React, { FCWithChildren, useContext, useEffect, useState } from 'react';
import dynamic from 'next/dynamic';
import useUser from '@hooks/user/use-user';
import useLocalStorage from '@hooks/use-local-storage';
import { useSiteConfirmation } from '@hooks/use-site-confirmation';
import { useShoppingPreferences } from '@hooks/user/use-shopping-preferences';

type RedirectUrlMode = { url: string; mode: 'push' | 'replace' };

interface AuthContextProps {
  setAuthOpen: (popinForced: boolean) => void;
  // redirectUrl: where to go when authentication is *not* done, modal dismissed
  setRedirectUrl: (type: RedirectUrlMode) => void;
  redirectUrl: RedirectUrlMode;
  closeAuthPopin: () => void;
  setOnAuth: (func: Function) => void;
  onAuth: Function;
  shouldDisplayAuthDialog: boolean;
  setShouldDisplayAuthDialog: React.Dispatch<boolean>;
  isAuthOpen: boolean;
  popinForced: boolean;
}

const AuthenticationContext = React.createContext<AuthContextProps>({} as never);

const AuthenticationModalV2: React.ComponentType<AuthenticationModalV2Props> = dynamic<AuthenticationModalV2Props>(
  () => import('@components/atoms/authentication/authentication-modal/authentication-modal-v2'),
  {
    ssr: false,
  },
);

const AuthenticationProvider: FCWithChildren = ({ children }) => {
  const { isAuthenticated } = useUser();
  const { hideLangPopup } = useSiteConfirmation();

  const [shouldDisplayAuthDialog, setShouldDisplayAuthDialog] = useLocalStorage<boolean>(
    'shouldDisplayAuthDialog',
    true,
  );

  const [redirectUrl, setRedirectUrl] = useState<RedirectUrlMode>();
  const [isOpen, setIsOpen] = useState<boolean>(false);
  // This state is used for tracking purposes
  const [popinForced, setPopinForced] = useState<boolean>(false);
  const [onAuth, setOnAuth] = useState<Function>(null);

  /*  Initialising Shopping Prefrences for Authenticated users.
  This has to be in the authentication context because the user context comes before the bucket context,
  and we have a feature flag called in the hook. */
  useShoppingPreferences();

  const setAuthOpen = (popinForced: boolean): void => {
    setIsOpen(true);
    setPopinForced(popinForced);
  };

  const closeAuthPopin = (): void => {
    setIsOpen(false);
  };

  // We've had issues with users being redirected before their information got back from the api, this is why we wait for this information.
  useEffect(() => {
    if (!isOpen && popinForced && !isAuthenticated && redirectUrl) {
      if (redirectUrl.mode === 'replace') {
        window.location.replace(redirectUrl.url);
      } else {
        window.location.href = redirectUrl.url;
      }
      setRedirectUrl(null);
    }
  }, [isOpen, popinForced, isAuthenticated]);

  useEffect(() => {
    if (isAuthenticated) {
      hideLangPopup();
    }
  }, [isAuthenticated]);

  useEffect(() => {
    if (isAuthenticated && !isOpen) {
      onAuth?.();
    }
  }, [isAuthenticated, isOpen]);

  const value = {
    setAuthOpen,
    setRedirectUrl,
    redirectUrl,
    closeAuthPopin,
    setOnAuth,
    onAuth,
    shouldDisplayAuthDialog,
    setShouldDisplayAuthDialog,
    isAuthOpen: isOpen,
    popinForced,
  };

  return (
    <AuthenticationContext.Provider value={value}>
      {/* TODO: this should work without isOpen here, but needs to be fixed inside VCAuth web component */}
      {isOpen && (
        <div
          className="wc-vc-authentication-v2"
          id="vc-authentication-v2"
        >
          <AuthenticationModalV2 modalType="welcome-screen" />
        </div>
      )}
      {children}
    </AuthenticationContext.Provider>
  );
};

const useAuthentication = () => useContext(AuthenticationContext);

export { AuthenticationProvider, useAuthentication };
